import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { SalaryRow } from '@chronos/shared';
import { useSalaryMutations, useSalarySheet } from '@/hooks/queries';
import { Card, ConfirmButton, EmptyState, ErrorNote, PageHeader, Spinner, StatTile, SubmitButton } from '@/components/ui';
import { MonthNav, currentMonthKey, monthLabel } from '@/pages/AttendancePage';
import { formatDateTime } from '@/lib/format';

/** Signed days: a grant may be negative, so "+-5" must not happen. */
const signed = (n: number) => `${n > 0 ? '+' : ''}${n}`;

/**
 * Admin-only. One place to seed and correct every employee's leave bank —
 * the cold-start screen: set joining dates (backdated as far as needed) and
 * grant the days people are bringing in from wherever leave was tracked before.
 * Reads the salary sheet, which already carries the bank per row; no money is
 * shown here.
 */
export function LeaveBankPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? currentMonthKey();
  const { data, isLoading, error } = useSalarySheet(month);
  const [open, setOpen] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const sum = (f: (r: SalaryRow) => number) => rows.reduce((s, r) => s + f(r), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Leave bank"
        description={`${monthLabel(month)} · every employee's paid-leave balance. Cold start: set each joining date to the day you began tracking in TickTock, then grant the leave people are carrying in.`}
        actions={<MonthNav month={month} onChange={(m) => setParams((p) => { p.set('month', m); return p; })} />}
      />

      <ErrorNote error={error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <Card><EmptyState title="No active employees" /></Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Employees" value={String(rows.length)} hint={`${rows.filter((r) => !r.joinedOn).length} without a joining date`} />
            <StatTile label="Carried in" value={String(sum((r) => r.bank.opening))} hint="days at start of month" />
            <StatTile label="Accrued + granted" value={signed(sum((r) => r.bank.accrued + r.bank.granted))} tone="green" />
            <StatTile label="Used" value={`−${sum((r) => r.bank.used)}`} />
            <StatTile label="Closing" value={String(sum((r) => r.bank.closing))} hint="days carried forward" tone="brand" />
          </div>

          <Card className="mt-4 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="th">Employee</th>
                    <th className="th">Joined</th>
                    <th className="th text-right">Opening</th>
                    <th className="th text-right">Accrued</th>
                    <th className="th text-right">Granted</th>
                    <th className="th text-right">Used</th>
                    <th className="th text-right">Unpaid</th>
                    <th className="th text-right">Closing</th>
                    <th className="th"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <Row key={r.userId} row={r} month={month} expanded={open === r.userId} onToggle={() => setOpen(open === r.userId ? null : r.userId)} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="mt-3 text-xs text-slate-400">
            Each month adds the paid-leave accrual from the joining date; leaves draw the bank down; the balance carries forward until the
            year-end month, when it is paid out on the <Link to="/salary" className="text-brand-700 hover:underline">salary sheet</Link>.
            Grants land in the month shown and carry forward from there — so an opening balance belongs in the month you start using TickTock.
          </p>
        </>
      )}
    </div>
  );
}

function Row({ row, month, expanded, onToggle }: { row: SalaryRow; month: string; expanded: boolean; onToggle: () => void }) {
  const { setSalary, addBankGrant, removeBankGrant } = useSalaryMutations();
  const [joined, setJoined] = useState(row.joinedOn ?? '');
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('Opening balance');
  const b = row.bank;
  const unpaid = Math.max(0, row.leaveUnits - b.used);

  const saveJoined = () => {
    if ((joined || null) === (row.joinedOn ?? null)) return;
    setSalary.mutate({ userId: row.userId, joinedOn: joined || null });
  };

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="td font-medium text-slate-900">
          <Link to={`/attendance?userId=${row.userId}&month=${month}`} className="text-brand-700 hover:underline">{row.userName}</Link>
        </td>
        <td className="td">
          <input
            type="date"
            className={`input max-w-[10rem] py-1 text-xs ${!row.joinedOn ? 'border-amber-400' : ''}`}
            value={joined}
            onChange={(e) => setJoined(e.target.value)}
            onBlur={saveJoined}
            title="Attendance and accrual start here. For staff who predate TickTock, use the day you started tracking here — not their original hire date — or every earlier month shows as leave."
          />
          {!row.joinedOn && <p className="mt-0.5 text-[10px] text-amber-700">not set — using account creation</p>}
        </td>
        <td className="td text-right tabular-nums">{b.opening}</td>
        <td className="td text-right tabular-nums text-emerald-700">+{b.accrued}</td>
        <td className={`td text-right tabular-nums ${b.granted ? 'font-semibold text-brand-700' : 'text-slate-400'}`}>{b.granted ? signed(b.granted) : '0'}</td>
        <td className={`td text-right tabular-nums ${b.used ? 'text-rose-700' : 'text-slate-400'}`}>{b.used ? `−${b.used}` : '0'}</td>
        <td className={`td text-right tabular-nums ${unpaid ? 'font-semibold text-rose-700' : 'text-slate-400'}`}>{unpaid || '0'}</td>
        <td className="td text-right font-semibold tabular-nums">{b.closing}{b.encashed && <span className="ml-1 badge bg-emerald-100 text-emerald-800">paid out</span>}</td>
        <td className="td text-right">
          <button className="btn-ghost px-2 py-1 text-xs" onClick={onToggle}>
            {expanded ? 'Close' : `Grant days${b.grants.length ? ` (${b.grants.length})` : ''}`}
          </button>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={9} className="px-4 py-3">
            <ErrorNote error={setSalary.error ?? addBankGrant.error ?? removeBankGrant.error} />
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const d = Number(days);
                if (!d || !reason.trim()) return;
                addBankGrant.mutate({ userId: row.userId, month, days: d, reason: reason.trim() }, { onSuccess: () => setDays('') });
              }}
            >
              <div>
                <label className="label">Days to add in {monthLabel(month)}</label>
                <input type="number" step={0.5} min={-365} max={365} className="input max-w-[7rem]" placeholder="e.g. 6" value={days} onChange={(e) => setDays(e.target.value)} autoFocus />
              </div>
              <div className="min-w-[14rem] flex-1">
                <label className="label">Reason</label>
                <input className="input" required maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              <SubmitButton pending={addBankGrant.isPending} className="btn-primary">Add to bank</SubmitButton>
            </form>

            {b.grants.length > 0 && (
              <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
                {b.grants.map((g) => (
                  <li key={g.id} className="flex items-center gap-3 px-3 py-2">
                    <span className={`w-12 text-right font-mono text-sm font-semibold tabular-nums ${g.days > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{g.days > 0 ? '+' : ''}{g.days}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{g.reason}</span>
                    <span className="text-xs text-slate-400">{g.createdByName ?? '—'} · {formatDateTime(g.createdAt)}</span>
                    <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Remove" confirmLabel="Confirm" onConfirm={() => removeBankGrant.mutate(g.id)} />
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
